import { HttpErrorResponse } from '@angular/common/http';
import { ErrorDto } from './types';

/**
 * No-man's land function that hopes to parse every possible error scenario from frontend & backend.
 * Handles simple error messages, HttpClient errors
 */
export function tryGetErrorMessage(err: unknown) {
  if (!err) return '';

  // Check if the error is an HTTP error caught by Angular HttpClient
  if (err instanceof HttpErrorResponse) {
    // Angular wraps HTTP errors in HttpErrorResponse objects
    const httpError = err as HttpErrorResponse;

    // If there's a backend message, return it
    const message =
      // We usually return our errors in an `error` property
      httpError.error?.error ||
      // Sometimes, it's in the `message` property
      httpError.error?.message;

    // If the error object contains a more detailed message or we have a statusText, use it
    if (typeof message === 'string') {
      // Try to parse the message as an ErrorDto (e.g. TheftReportError)
      try {
        const errorObject = JSON.parse(message) as ErrorDto;
        if ('message' in errorObject) return errorObject.message;
      } catch {
        return message;
      }

      return message;
    }

    if (httpError.status === 500 || httpError.status === 0) {
      return 'An unexpected error occurred. Please try again later. If the problem persists, contact support.';
    }

    // If the message is an object, try to stringify it
    if (typeof message === 'object') {
      const details = JSON.stringify(message);
      // Concatenate status text if available
      return `${httpError.statusText}: ${details}`;
    }

    // Otherwise, the message will probably be available from the angular object
    if (httpError.message) return httpError.message;

    // Fall back to error statusText
    return httpError.statusText || 'Unknown HTTP error';
  }

  // Next, check if the error is of type Error
  if (err instanceof Error) {
    const error = err as Error;
    return error.message;
  }

  // Next, check if the error has a property called message
  // (it can have message but not be of type Error)
  if (Object.prototype.hasOwnProperty.call(err, 'message')) {
    return (err as { message: string }).message;
  }

  // Next, check if the error is just a string
  if (typeof err === 'string') {
    return err;
  }

  // Last resort, return a hardcoded unknown error message
  return 'Unknown error';
}

export function hasStatus(error: unknown): error is { status: number } {
  return typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number';
}
